import { Row, Col, Form, Button, Spinner } from 'react-bootstrap'
import { prependBackendUrl } from '../../../gql/client'

interface Props {
    editOrg: any
    form: any
    handleChange: (field: string, value: string) => void
    logoPreview: string | null
    setLogoPreview: (v: string | null) => void
    selectedLogo: File | null
    setSelectedLogo: (v: File | null) => void
    logoLoadError: boolean
    setLogoLoadError: (v: boolean) => void
    logoInputRef: React.RefObject<HTMLInputElement>
    handleLogoSelect: (e: React.ChangeEvent<HTMLInputElement>) => void
    handleLogoUpload: (orgId: string) => void
    uploadingLogo: boolean
    tplLogoPreview: string | null
    setTplLogoPreview: (v: string | null) => void
    selectedTplLogo: File | null
    setSelectedTplLogo: (v: File | null) => void
    tplLogoLoadError: boolean
    setTplLogoLoadError: (v: boolean) => void
    tplLogoInputRef: React.RefObject<HTMLInputElement>
    handleTplLogoSelect: (e: React.ChangeEvent<HTMLInputElement>) => void
    handleTplLogoUpload: (orgId: string) => void
    uploadingTplLogo: boolean
}

export const OrganizationGeneralForm: React.FC<Props> = ({
    editOrg, form, handleChange,
    logoPreview, setLogoPreview, selectedLogo, setSelectedLogo, logoLoadError, setLogoLoadError,
    logoInputRef, handleLogoSelect, handleLogoUpload, uploadingLogo,
    tplLogoPreview, setTplLogoPreview, selectedTplLogo, setSelectedTplLogo, tplLogoLoadError, setTplLogoLoadError,
    tplLogoInputRef, handleTplLogoSelect, handleTplLogoUpload, uploadingTplLogo,
}) => {
    return (
        <>
            {/* Logos in 2 columns */}
            <Row className="mb-5 pb-4 border-bottom">
                <Col md={6}>
                    <div className="d-flex align-items-center gap-3">
                        <div>
                            {(logoPreview || editOrg?.logo) && !logoLoadError ? (
                                <img
                                    src={logoPreview || prependBackendUrl(editOrg?.logo)}
                                    alt="Logo"
                                    style={{ width: 70, height: 70, objectFit: 'contain', borderRadius: 10, border: '2px solid #eee' }}
                                    onError={() => setLogoLoadError(true)}
                                />
                            ) : (
                                <div
                                    className="bg-light d-flex align-items-center justify-content-center"
                                    style={{ width: 70, height: 70, borderRadius: 10, border: '2px dashed #ccc' }}
                                >
                                    <i className="bi bi-image text-muted fs-2"></i>
                                </div>
                            )}
                        </div>
                        <div>
                            <h6 className="fw-semibold mb-1 fs-7">Organization Logo</h6>
                            <p className="text-muted mb-2" style={{ fontSize: '0.75rem' }}>JPG, PNG or WebP. Max 2MB.</p>
                            <div className="d-flex gap-1 flex-wrap">
                                <Button variant="outline-primary" size="sm" onClick={() => logoInputRef.current?.click()}>
                                    <i className="bi bi-upload me-1"></i>
                                    {editOrg?.logo || logoPreview ? 'Change' : 'Upload'}
                                </Button>
                                {(logoPreview || selectedLogo) && editOrg && (
                                    <Button variant="outline-success" size="sm" onClick={() => handleLogoUpload(editOrg.id)} disabled={uploadingLogo}>
                                        {uploadingLogo ? <Spinner animation="border" size="sm" /> : <i className="bi bi-check-lg"></i>}
                                    </Button>
                                )}
                                {logoPreview && (
                                    <Button variant="outline-secondary" size="sm" onClick={() => { setLogoPreview(null); setSelectedLogo(null) }}>
                                        <i className="bi bi-x-lg"></i>
                                    </Button>
                                )}
                            </div>
                            <input ref={logoInputRef} type="file" accept=".png,.jpg,.jpeg,.webp" onChange={handleLogoSelect} style={{ display: 'none' }} />
                        </div>
                    </div>
                </Col>
                <Col md={6}>
                    <div className="d-flex align-items-center gap-3">
                        <div>
                            {(tplLogoPreview || editOrg?.templateLogo) && !tplLogoLoadError ? (
                                <img
                                    src={tplLogoPreview || prependBackendUrl(editOrg?.templateLogo)}
                                    alt="Template Logo"
                                    style={{ width: 70, height: 70, objectFit: 'contain', borderRadius: 10, border: '2px solid #eee' }}
                                    onError={() => setTplLogoLoadError(true)}
                                />
                            ) : (
                                <div
                                    className="bg-light d-flex align-items-center justify-content-center"
                                    style={{ width: 70, height: 70, borderRadius: 10, border: '2px dashed #ccc' }}
                                >
                                    <i className="bi bi-file-earmark-image text-muted fs-2"></i>
                                </div>
                            )}
                        </div>
                        <div>
                            <h6 className="fw-semibold mb-1 fs-7">Template Logo</h6>
                            <p className="text-muted mb-2" style={{ fontSize: '0.75rem' }}>For document templates.</p>
                            <div className="d-flex gap-1 flex-wrap">
                                <Button variant="outline-primary" size="sm" onClick={() => tplLogoInputRef.current?.click()}>
                                    <i className="bi bi-upload me-1"></i>
                                    {editOrg?.templateLogo || tplLogoPreview ? 'Change' : 'Upload'}
                                </Button>
                                {(tplLogoPreview || selectedTplLogo) && editOrg && (
                                    <Button variant="outline-success" size="sm" onClick={() => handleTplLogoUpload(editOrg.id)} disabled={uploadingTplLogo}>
                                        {uploadingTplLogo ? <Spinner animation="border" size="sm" /> : <i className="bi bi-check-lg"></i>}
                                    </Button>
                                )}
                                {tplLogoPreview && (
                                    <Button variant="outline-secondary" size="sm" onClick={() => { setTplLogoPreview(null); setSelectedTplLogo(null) }}>
                                        <i className="bi bi-x-lg"></i>
                                    </Button>
                                )}
                            </div>
                            <input ref={tplLogoInputRef} type="file" accept=".png,.jpg,.jpeg,.webp" onChange={handleTplLogoSelect} style={{ display: 'none' }} />
                        </div>
                    </div>
                </Col>
            </Row>

            <Row className="g-4">
                <Col md={6}>
                    <Form.Group>
                        <Form.Label className="fw-semibold text-gray-700 required">Name</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Organization name"
                            value={form.name}
                            onChange={(e) => handleChange('name', e.target.value)}
                            required
                        />
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group>
                        <Form.Label className="fw-semibold text-gray-700 required">Owner</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Agency owner name"
                            value={form.owner}
                            onChange={(e) => handleChange('owner', e.target.value)}
                            required
                        />
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group>
                        <Form.Label className="fw-semibold text-gray-700 required">Email</Form.Label>
                        <Form.Control
                            type="email"
                            placeholder="contact@organization.com"
                            value={form.email}
                            onChange={(e) => handleChange('email', e.target.value)}
                            required
                        />
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group>
                        <Form.Label className="fw-semibold text-gray-700 required">Phone</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="+1 (000) 000-0000"
                            value={form.phone}
                            onChange={(e) => handleChange('phone', e.target.value)}
                            required
                        />
                    </Form.Group>
                </Col>
                <Col md={12}>
                    <Form.Group>
                        <Form.Label className="fw-semibold text-gray-700">Website</Form.Label>
                        <Form.Control
                            type="url"
                            placeholder="https://www.example.com"
                            value={form.website}
                            onChange={(e) => handleChange('website', e.target.value)}
                        />
                    </Form.Group>
                </Col>
                <Col xs={12}><hr className="my-1" /></Col>
                <Col md={12}>
                    <Form.Group>
                        <Form.Label className="fw-semibold text-gray-700 required">Address</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Street address"
                            value={form.streetAddress}
                            onChange={(e) => handleChange('streetAddress', e.target.value)}
                            required
                        />
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group>
                        <Form.Label className="fw-semibold text-gray-700 required">City</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="City"
                            value={form.city}
                            onChange={(e) => handleChange('city', e.target.value)}
                            required
                        />
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group>
                        <Form.Label className="fw-semibold text-gray-700">State</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="FL"
                            maxLength={2}
                            value={form.state}
                            onChange={(e) => handleChange('state', e.target.value.toUpperCase())}
                        />
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group>
                        <Form.Label className="fw-semibold text-gray-700 required">Postal Code</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="33166"
                            value={form.postalCode}
                            onChange={(e) => handleChange('postalCode', e.target.value)}
                            required
                        />
                    </Form.Group>
                </Col>
                <Col md={12}>
                    <Form.Group>
                        <Form.Label className="fw-semibold text-gray-700 required">Country</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Country"
                            value={form.country}
                            onChange={(e) => handleChange('country', e.target.value)}
                            required
                        />
                    </Form.Group>
                </Col>
            </Row>
        </>
    )
}
